import { useCallback, useMemo, useRef, useState } from 'react';
import { toastr } from 'react-redux-toastr';
import useIntl from '../../../hooks/useIntl';
import messages from './messages';
import appMessages from '../../App/messages';
import {
  useAsyncResetPortalAccount,
  useAsyncSendPortalClaimEmail,
  useAsyncSendPortalPasswordResetEmail,
  useAsyncTogglePortalAccountStatus,
  useAsyncUpdatePortalUser,
} from '../../../components/EditOccupantModal/ResidentPortal/hooks';
import {
  PORTAL_ACTIONS,
  buildPortalActionOptions,
  buildPortalStatusBanners,
  getConfirmationCopy,
} from './portalActions';

const NO_ACTION = '';

// The legacy hooks were written against the legacy panel's local state and
// still expect these setters; the Mini-Profile refreshes through React Query
// instead, so they are absorbed here rather than threaded through.
const noop = () => {};

// Those same hooks call their `toastr` argument as a FUNCTION taking one
// {title, message, type} payload — the shape of App/actions' promptToaster,
// which is what the legacy panel handed them. react-redux-toastr's own
// export is an object of methods, so passing it straight through throws
// inside the handler *after* the request has already succeeded, which both
// swallows the toast and skips the caller's refresh.
//
// Each handler gets its own adapter so the success text can name the action
// that ran, replacing the legacy strings that collide across actions. The
// error text stays as the handler built it — it carries the thrown message.
const buildToastAdapter =
  (formatMessage, successMessage) =>
  ({ message, type } = {}) =>
    type === 'error'
      ? toastr.error(formatMessage(appMessages.error), message)
      : toastr.success(
          formatMessage(appMessages.success),
          formatMessage(successMessage),
        );

// A `= {}` default allocates a fresh object every render where the caller
// passes undefined, which would defeat the useMemo calls below (and the
// sections memo in useMiniProfileSections that depends on this hook's
// return) on every render. One stable module-level instance instead.
const EMPTY_PORTAL_STATUS = {};

const usePortalAccountActions = ({
  organizationId,
  propertyId,
  customerId,
  portalUserId,
  portalStatus = EMPTY_PORTAL_STATUS,
  hasPortalStatus,
  portalStatusFailed,
  memberStatus,
  endDate,
  onActionComplete,
  isApplicantSubject = false,
  // Resolved by the caller, the only place that knows the AffordaPortal and Head-of-Household state.
  sendInviteBlockedByPrimary = false,
}) => {
  const [selectedAction, setSelectedAction] = useState(NO_ACTION);
  const [pendingAction, setPendingAction] = useState(null);
  const { formatMessage } = useIntl();

  // One endpoint toggles both directions, so which way it will go is only
  // knowable from the account's current state.
  const blockToggleSuccess = portalStatus.canUnblockAccount
    ? messages.portalAccountUnblocked
    : messages.portalAccountBlocked;

  const { handleResetPortalAccount } = useAsyncResetPortalAccount(
    organizationId,
    propertyId,
    portalUserId,
    portalStatus,
    noop,
    noop,
    noop,
    buildToastAdapter(formatMessage, messages.portalAccountReset),
  );
  const { handleSendPortalClaimEmail } = useAsyncSendPortalClaimEmail(
    organizationId,
    propertyId,
    customerId,
    noop,
    buildToastAdapter(formatMessage, messages.portalInviteSent),
    isApplicantSubject ? 'applicant' : 'resident',
  );
  const { handleTogglePortalAccountStatus } = useAsyncTogglePortalAccountStatus(
    organizationId,
    propertyId,
    portalUserId,
    portalStatus,
    noop,
    noop,
    buildToastAdapter(formatMessage, blockToggleSuccess),
  );
  const { handleSendPortalPasswordResetEmail } =
    useAsyncSendPortalPasswordResetEmail(
      organizationId,
      propertyId,
      portalStatus.emailAddress,
      noop,
      buildToastAdapter(formatMessage, messages.portalPasswordResetSent),
    );
  const { handleUpdatePortalUser } = useAsyncUpdatePortalUser(
    organizationId,
    propertyId,
    portalUserId,
    // Only the counter. The legacy panel also sent isActive: true here, which
    // silently unblocks an account that was blocked on purpose -- reachable
    // because the lockout gate is independent of isActive, and because the
    // counter can only reach 10 while the account is still active. Unblocking
    // has its own action.
    { numberFailedLoginAttempts: 0 },
    portalStatus,
    noop,
    noop,
    buildToastAdapter(formatMessage, messages.portalLoginAttemptsReset),
  );

  // The five legacy handlers are plain arrows declared in their own hook
  // bodies, so they are new functions on every render and the spec requires
  // reusing those hooks unmodified. Depending on them would make runAction --
  // and through it onActionChange, which useMiniProfileSections treats as a
  // stable leaf of its sections memo -- change identity every render. Read
  // them through a ref that is refreshed each render instead.
  const handlersRef = useRef(null);
  handlersRef.current = {
    [PORTAL_ACTIONS.SEND_INVITE]: handleSendPortalClaimEmail,
    [PORTAL_ACTIONS.RESET_PASSWORD]: handleSendPortalPasswordResetEmail,
    [PORTAL_ACTIONS.TOGGLE_BLOCK]: handleTogglePortalAccountStatus,
    [PORTAL_ACTIONS.RESET_ACCOUNT]: handleResetPortalAccount,
    [PORTAL_ACTIONS.RESET_LOGIN_ATTEMPTS]: handleUpdatePortalUser,
  };
  const onActionCompleteRef = useRef(null);
  onActionCompleteRef.current = onActionComplete;

  // The select clears itself the moment an action starts, so nothing else
  // stops a second pick while the first request is still open -- two quick
  // picks on the block toggle would fire concurrent writes and leave the
  // account wherever they happen to land. The legacy handlers' own
  // isSubmitting is not returned to us, hence a guard of our own.
  const isRunningRef = useRef(false);

  const runAction = useCallback(async (action) => {
    const run = handlersRef.current[action];

    if (!run || isRunningRef.current) return;
    isRunningRef.current = true;
    try {
      await run();
      await onActionCompleteRef.current?.();
    } finally {
      isRunningRef.current = false;
    }
  }, []);

  const onActionChange = useCallback(
    async (action) => {
      if (getConfirmationCopy(action, portalStatus, formatMessage)) {
        setSelectedAction(action);
        setPendingAction(action);
        return;
      }
      // Reset before running: the select is a trigger, not a value, so the
      // same action has to stay re-selectable.
      setSelectedAction(NO_ACTION);
      await runAction(action);
    },
    [portalStatus, runAction, formatMessage],
  );

  const onCancel = useCallback(() => {
    setPendingAction(null);
    setSelectedAction(NO_ACTION);
  }, []);

  const onConfirm = useCallback(async () => {
    const action = pendingAction;
    setPendingAction(null);
    setSelectedAction(NO_ACTION);
    await runAction(action);
  }, [pendingAction, runAction]);

  const confirmationCopy = pendingAction
    ? getConfirmationCopy(pendingAction, portalStatus, formatMessage)
    : null;

  return {
    // React Query keeps the last good data when a refetch fails, so without
    // this the error banner would sit above a list of actions still enabled
    // from capabilities we already know are stale.
    options: useMemo(
      () =>
        buildPortalActionOptions(
          portalStatusFailed ? EMPTY_PORTAL_STATUS : portalStatus,
          formatMessage,
          {
            sendInviteBlockedByPrimary,
            isApplicantSubject,
          },
        ),
      [
        portalStatus,
        portalStatusFailed,
        formatMessage,
        sendInviteBlockedByPrimary,
        isApplicantSubject,
      ],
    ),
    banners: useMemo(
      () =>
        buildPortalStatusBanners({
          portalStatus,
          memberStatus,
          endDate,
          hasPortalStatus,
          portalStatusFailed,
          isApplicantSubject,
          formatMessage,
        }),
      [
        portalStatus,
        memberStatus,
        endDate,
        hasPortalStatus,
        portalStatusFailed,
        isApplicantSubject,
        formatMessage,
      ],
    ),
    selectedAction,
    onActionChange,
    confirmation: {
      open: Boolean(pendingAction),
      title: confirmationCopy?.title ?? '',
      body: confirmationCopy?.body ?? '',
      confirmLabel: confirmationCopy?.confirmLabel ?? '',
      onCancel,
      onConfirm,
    },
  };
};

export default usePortalAccountActions;
